import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useAuthStore } from '../../store';
import type { UserRole } from '../../types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading, tokens, user } = useAuthStore();

  // Show loading while checking auth state (e.g., during token refresh)
  if (isLoading && tokens) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#1a1d24',
        }}
      >
        <CircularProgress sx={{ color: '#4ECDC4' }} />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if required
  if (requiredRoles && requiredRoles.length > 0) {
    const userRoles = user?.roles ?? (user?.role ? [user.role] : []);
    if (!userRoles.length || !requiredRoles.some((r) => userRoles.includes(r))) {
      // User doesn't have required role - show access denied
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            gap: 2,
          }}
        >
          <Typography variant="h4" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You do not have permission to access this page.
          </Typography>
          <Button variant="contained" href="/">
            Return to Dashboard
          </Button>
        </Box>
      );
    }
  }

  return <>{children}</>;
}
